import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { FaArrowLeft, FaPen, FaArrowUp, FaArrowDown, FaUser, FaUsers, FaPlus, FaPhone, FaIdBadge, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import Preloader from './Preloader';
import { Autocomplete } from '@react-google-maps/api';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';

const libraries = ['places', 'marker'];
// Use the same visual style behavior as the main Incident Map

function IncidentReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null); // Incident data
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  // Marker rendered directly in JSX
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    type: '',
    location: '',
    details: '',
    image_url: '',
    location_coords: null,
    severity: 'minimal',
    status: 'active'
  });
  const [assignedResponders, setAssignedResponders] = useState([]);
  const [availableResponders, setAvailableResponders] = useState([]);
  const [respondersLoading, setRespondersLoading] = useState(false);
  const [showResponderAssignment, setShowResponderAssignment] = useState(false);

  const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '8px'
  };

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    styles: [
      { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
      { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
      { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
      { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
      { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
      { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
      { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
      { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
      { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
      { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] }
    ]
  }), []);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyAugo7vMiL-tP9dyz6mlCvcZqcovWJl1eA",
    libraries
  });

  useEffect(() => {
      document.title = "Incident Report Detail";
    }, []);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchIncidentDetails();
      fetchIncidentResponders();
    }
  }, [currentUser, id]);

  // No imperative marker creation needed

  async function fetchIncidentDetails() {
    try {
      const { data, error } = await supabase
        .from('incident_reports')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      console.log('Incident data:', data);
      setIncident(data);
      
      // Set map center if coordinates exist
      if (data.location_coords) {
        try {
          console.log('Location coordinates:', data.location_coords);
          const coords = data.location_coords;
          
          if (coords.latitude && coords.longitude) {
            setMapCenter({
              lat: parseFloat(coords.latitude),
              lng: parseFloat(coords.longitude)
            });
          } else {
            console.log('Missing latitude or longitude in coordinates');
          }
        } catch (e) {
          console.error('Error processing coordinates:', e);
        }
      } else {
        console.log('No location_coords found in data');
      }
    } catch (error) {
      console.error('Error fetching incident details:', error);
      alert('Failed to load incident details');
    } finally {
      setLoading(false);
    }
  }

  // No onLoad handler required

  function openForm(incident = null) {
    if (incident) {
      setForm({
        type: incident.type || '',
        location: incident.location || '',
        details: incident.details || '',
        image_url: incident.image_url || '',
        location_coords: incident.location_coords || null,
        severity: incident.severity || 'minimal',
        status: incident.status || 'active'
      });
    }
    setShowForm(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from('incident_reports')
        .update(form)
        .eq('id', id);

      if (error) throw error;
      // Audit log for edit/archive
      const actionType = form.status === 'archived' ? 'archived incident report' : 'edited incident report';
      await supabase.from('audit_logs').insert([{
        user_id: currentUser.id,
        action: actionType,
        target_id: id,
        target_type: 'IncidentReports',
        details: form
      }]);
      alert('Incident updated successfully!');
      setShowForm(false);
      fetchIncidentDetails();
    } catch (error) {
      console.error('Error updating incident:', error);
      alert(`Failed to update incident: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function fetchIncidentResponders() {
    setRespondersLoading(true);
    try {
      // 1. Fetch responders currently assigned to this incident from responder_incident_responses
      const { data: assignedResponders, error: assignedError } = await supabase
        .from('responder_incident_responses')
        .select(`
          id,
          status,
          accepted_at,
          assignment_type,
          approved_responders!inner (
            id,
            user_id,
            first_name,
            last_name,
            responder_id,
            profession,
            mobile_number,
            email
          )
        `)
        .eq('incident_id', id)
        .in('status', ['accepted', 'responding', 'on_scene']);

      // 2. Fetch all available responders for assignment
      const { data: allResponders, error: respondersError } = await supabase
        .from('approved_responders')
        .select('*')
        .eq('is_active', true)
        .eq('is_available', true)
        .order('first_name');

      if (respondersError) {
        console.error('Error fetching available responders:', respondersError);
      }

      // 3. Fallback: Check legacy assignments from audit logs and SOS beacons
      const { data: auditResponders, error: auditError } = await supabase
        .from('audit_logs')
        .select(`
          user_id,
          created_at,
          details
        `)
        .eq('target_type', 'IncidentReports')
        .eq('target_id', id)
        .eq('action', 'assigned to incident');

      const { data: sosResponders, error: sosError } = await supabase
        .from('sos_beacons')
        .select(`
          responder_id,
          profiles!sos_beacons_responder_id_fkey (
            id,
            first_name,
            last_name,
            user_id
          )
        `)
        .eq('location', incident?.location)
        .eq('status', 'responding')
        .not('responder_id', 'is', null);

      // Transform assigned responders data
      const currentlyAssigned = [];
      const userIds = new Set();

      // Add responders from responder_incident_responses table
      if (assignedResponders && !assignedError) {
        assignedResponders.forEach(item => {
          const responder = {
            id: item.id,
            user_id: item.approved_responders.user_id,
            first_name: item.approved_responders.first_name,
            last_name: item.approved_responders.last_name,
            responder_id: item.approved_responders.responder_id,
            profession: item.approved_responders.profession,
            mobile_number: item.approved_responders.mobile_number,
            email: item.approved_responders.email,
            assignment_type: item.assignment_type || 'Assigned',
            status: item.status,
            accepted_at: item.accepted_at,
            assignment_source: 'responder_incident_responses'
          };
          currentlyAssigned.push(responder);
          userIds.add(responder.user_id);
        });
      }

      // Add SOS responders (if not already assigned)
      if (sosResponders && !sosError) {
        sosResponders.forEach(sos => {
          if (sos.profiles && !userIds.has(sos.profiles.user_id)) {
            currentlyAssigned.push({
              ...sos.profiles,
              assignment_type: 'SOS Response',
              assignment_source: 'sos_beacon'
            });
            userIds.add(sos.profiles.user_id);
          }
        });
      }

      // Add legacy audit log assignments (if not already assigned)
      if (auditResponders && !auditError) {
        auditResponders.forEach(audit => {
          if (!userIds.has(audit.user_id)) {
            currentlyAssigned.push({
              user_id: audit.user_id,
              first_name: audit.details?.responder_name?.split(' ')[0] || 'Unknown',
              last_name: audit.details?.responder_name?.split(' ').slice(1).join(' ') || '',
              assignment_type: 'Legacy Assignment',
              status: 'assigned',
              accepted_at: audit.created_at,
              assignment_source: 'audit_log'
            });
            userIds.add(audit.user_id);
          }
        });
      }

      console.log('Currently Assigned Responders:', currentlyAssigned);
      console.log('Available Responders:', allResponders);

      setAssignedResponders(currentlyAssigned);
      setAvailableResponders(allResponders || []);

    } catch (error) {
      console.error('Error fetching incident responders:', error);
      setAssignedResponders([]);
      setAvailableResponders([]);
    } finally {
      setRespondersLoading(false);
    }
  }

  async function assignResponderToIncident(responder) {
    try {
      setRespondersLoading(true);
      
      // Check if responder is already assigned to this incident
      const { data: existingAssignment, error: checkError } = await supabase
        .from('responder_incident_responses')
        .select('id')
        .eq('responder_id', responder.id)
        .eq('incident_id', id)
        .in('status', ['accepted', 'responding', 'on_scene'])
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingAssignment) {
        alert(`${responder.first_name} ${responder.last_name} is already assigned to this incident.`);
        return;
      }

      // Create incident response record
      const { error: responseError } = await supabase
        .from('responder_incident_responses')
        .insert([{
          responder_id: responder.id,
          incident_id: id,
          accepted_at: new Date().toISOString(),
          status: 'accepted',
          assignment_type: 'admin_assigned'
        }]);

      if (responseError) {
        console.error('Error creating incident response:', responseError);
        throw responseError;
      }

      // Update responder availability if the function exists
      try {
        const { error: availabilityError } = await supabase
          .rpc('update_responder_availability', {
            responder_bigint: responder.id,
            new_status: 'responding',
            incident_id: parseInt(id)
          });

        if (availabilityError) {
          console.log('Availability update failed (function may not exist):', availabilityError);
        }
      } catch (funcError) {
        console.log('Availability function not available:', funcError);
      }

      // Ensure mobile dashboard immediately reflects "Responding" state
      try {
        await supabase
          .from('responder_incident_responses')
          .upsert([
            {
              responder_id: responder.id,
              incident_id: id,
              accepted_at: new Date().toISOString(),
              status: 'responding',
              notes: 'Assigned by admin'
            }
          ], { onConflict: 'responder_id,incident_id' });
      } catch (upsertError) {
        console.log('Upsert responding status failed (possibly missing unique constraint):', upsertError);
      }

      // Optional: keep legacy/other UIs in sync
      try {
        await supabase
          .from('incident_reports')
          .update({
            status: 'responding',
            responder_id: responder.id,
            response_status: 'responding',
            responded_at: new Date().toISOString()
          })
          .eq('id', id);
      } catch (legacyUpdateError) {
        console.log('Optional incident_reports compatibility update skipped:', legacyUpdateError);
      }

      // Log the assignment in audit logs
      const { error: auditError } = await supabase
        .from('audit_logs')
        .insert([{
          user_id: responder.user_id,
          action: 'assigned to incident',
          target_id: id,
          target_type: 'IncidentReports',
          details: {
            incident_location: incident?.location,
            incident_type: incident?.type,
            assigned_by: currentUser.id,
            responder_name: `${responder.first_name} ${responder.last_name}`,
            responder_id: responder.responder_id
          }
        }]);

      if (auditError) {
        console.error('Error logging assignment:', auditError);
        // Don't throw error for audit logs, just log it
      }

      // Update incident status to 'responding' if it's not already
      if (incident.status === 'active') {
        const { error: statusError } = await supabase
          .from('incident_reports')
          .update({ status: 'responding' })
          .eq('id', id);

        if (statusError) {
          console.error('Error updating incident status:', statusError);
        } else {
          // Update local state
          setIncident(prev => ({ ...prev, status: 'responding' }));
        }
      }

      // Refresh the responders list
      await fetchIncidentResponders();
      
      alert(`Successfully assigned ${responder.first_name} ${responder.last_name} to this incident.`);

    } catch (error) {
      console.error('Error assigning responder:', error);
      alert('Failed to assign responder to incident. Please try again.');
    } finally {
      setRespondersLoading(false);
    }
  }

  async function removeResponderFromIncident(responder) {
    try {
      setRespondersLoading(true);
      
      // Get the exact response record to update availability correctly
      const { data: responseRecord, error: fetchResponseError } = await supabase
        .from('responder_incident_responses')
        .select('id, responder_id, incident_id')
        .eq('id', responder.id)
        .single();

      if (fetchResponseError) {
        console.log('Could not fetch response record for removal, proceeding with best effort:', fetchResponseError);
      }

      // 1) Cancel/remove the active response record
      const { error: cancelError } = await supabase
        .from('responder_incident_responses')
        .update({ status: 'cancelled' })
        .eq('id', responder.id);

      if (cancelError) {
        throw cancelError;
      }

      // 2) Set availability back to available (best-effort)
      try {
        if (responseRecord?.responder_id) {
          await supabase.rpc('update_responder_availability', {
            responder_bigint: responseRecord.responder_id,
            new_status: 'available',
            incident_id: null
          });
        }
      } catch (availabilityResetError) {
        console.log('Availability reset skipped:', availabilityResetError);
      }

      // 2.5) If no more active responders remain for this incident, revert status
      try {
        const { count: activeCount, error: remainingError } = await supabase
          .from('responder_incident_responses')
          .select('id', { count: 'exact', head: true })
          .eq('incident_id', id)
          .in('status', ['accepted', 'responding', 'on_scene']);

        if (!remainingError && (activeCount ?? 0) === 0) {
          try {
            await supabase
              .from('incident_reports')
              .update({
                status: 'active',
                responder_id: null,
                response_status: null,
                responded_at: null
              })
              .eq('id', id);
            // Update local state
            setIncident(prev => ({ ...prev, status: 'active' }));
          } catch (incidentUpdateError) {
            console.log('Optional incident status revert skipped:', incidentUpdateError);
          }
        }
      } catch (remainingCheckError) {
        console.log('Remaining responders check failed:', remainingCheckError);
      }

      // 3) Log the removal in audit logs (non-blocking)
      try {
        await supabase
          .from('audit_logs')
          .insert([{
            user_id: currentUser.id,
            action: 'removed responder from incident',
            target_id: id,
            target_type: 'IncidentReports',
            details: {
              incident_location: incident?.location,
              incident_type: incident?.type,
              removed_responder: `${responder.first_name} ${responder.last_name}`,
              response_id: responder.id,
              approved_responder_id: responseRecord?.responder_id || null
            }
          }]);
      } catch (auditError) {
        console.log('Audit log write skipped:', auditError);
      }

      // Refresh the responders list
      await fetchIncidentResponders();
      
      alert(`Successfully removed ${responder.first_name} ${responder.last_name} from this incident.`);

    } catch (error) {
      console.error('Error removing responder:', error);
      alert('Failed to remove responder from incident.');
    } finally {
      setRespondersLoading(false);
    }
  }

  async function handleImageUpload(event) {
    const file = event.target.files[0];
    if (file) {
      try {
        // Generate a unique file name using timestamp and random string
        const timestamp = new Date().getTime();
        const randomString = Math.random().toString(36).substring(7);
        const fileExt = file.name.split('.').pop();
        const fileName = `${timestamp}-${randomString}.${fileExt}`;
        const filePath = `incidents/${fileName}`;

        // Upload to Supabase storage
        const { error: uploadError } = await supabase.storage
          .from('incident-images')
          .upload(filePath, file);

        if (uploadError) {
          console.error('Error uploading image:', uploadError);
          alert('Failed to upload image. Please try again.');
          return;
        }

        // Get the public URL of the uploaded image
        const { data: { publicUrl } } = supabase.storage
          .from('incident-images')
          .getPublicUrl(filePath);

        // Update the form state with the new image URL
        setForm(prev => ({
          ...prev,
          image_url: publicUrl
        }));

      } catch (error) {
        console.error('Error in handleImageUpload:', error);
        alert('Failed to upload image. Please try again.');
      }
    }
  }

  if (!currentUser || loading) {
    return <Preloader text="Loading Details..." />;
  }

  if (!incident) {
    return <div>Incident not found</div>;
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32 }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
          <button
            onClick={() => navigate('/incident-reports')}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              fontSize: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaArrowLeft /> 
          </button>
          <div className="title-text">Incident Report Details</div>
        </div>
        <ProfileMenu />
      </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px #0001' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 24 }}>
            {currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin' || (currentUser.role === 'barangay' && incident.user_id === currentUser.id)) && (
              <button
                onClick={() => openForm(incident)}
                style={{
                  background: '#19c37d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content'
                }}
              >
                <FaPen /> Edit
              </button>
            )}
          </div>
          {/* Row: Image and Map */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 24,
            marginBottom: 32
          }}>
            <div style={{ flex: '1 1 400px', minWidth: '300px' }}>
              {isLoaded && mapCenter ? (
                <GoogleMap
                  mapContainerStyle={{
                    width: '100%',
                    height: '420px',           
                    borderRadius: '8px'
                  }}
                  center={mapCenter}
                  zoom={15}
                  options={mapOptions}
                >
                  <Marker position={mapCenter} />
                </GoogleMap>
              ) : (
                <div style={{
                  height: 420,                
                  borderRadius: 8,
                  background: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666'
                }}>
                  {isLoaded ? 'No location coordinates available' : 'Loading map...'}
                </div>
              )}
            </div>
            <div style={{ flex: '1 1 400px', minWidth: '300px' }}>
              {incident.image_url && (
                <img
                  src={incident.image_url}
                  alt="Incident"
                  style={{ width: '100%', height: 420, objectFit: 'cover', borderRadius: 8 }} // Increased height
                />
              )}
            </div>
          </div>
          {/* Details below */}
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Type</label>
              <span style={{
                padding: '4px 8px',
                borderRadius: 4,
                fontSize: 14,
                background: (incident.type || '') === 'fire' ? '#fce8e6' :
                           (incident.type || '') === 'earthquake' ? '#e6f4ea' :
                           (incident.type || '') === 'eruption' ? '#fff3cd' :
                           (incident.type || '') === 'tsunami' ? '#e8f0fe' : '#f8d7da',
                color: (incident.type || '') === 'fire' ? '#d32f2f' :
                       (incident.type || '') === 'earthquake' ? '#1e7e34' :
                       (incident.type || '') === 'eruption' ? '#856404' :
                       (incident.type || '') === 'tsunami' ? '#1a73e8' : '#721c24'
              }}>
                {((incident.type || '').charAt(0).toUpperCase() + (incident.type || '').slice(1))}
              </span>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Location</label>
              <div style={{ fontSize: 16 }}>{incident.location}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Details</label>
              <div style={{ fontSize: 16, whiteSpace: 'pre-wrap' }}>{incident.details}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Severity</label>
              <span style={{
                padding: '4px 8px',
                borderRadius: 4,
                fontSize: 14,
                background: (incident.severity || '') === 'minimal' ? '#e6f4ea' :
                           (incident.severity || '') === 'moderate' ? '#fff3cd' :
                           (incident.severity || '') === 'severe' ? '#fce8e6' :
                           (incident.severity || '') === 'critical' ? '#f8d7da' :
                           (incident.severity || '') === 'catastrophic' ? '#721c24' : '#f8d7da',
                color: (incident.severity || '') === 'minimal' ? '#1e7e34' :
                       (incident.severity || '') === 'moderate' ? '#856404' :
                       (incident.severity || '') === 'severe' ? '#d32f2f' :
                       (incident.severity || '') === 'critical' ? '#721c24' :
                       (incident.severity || '') === 'catastrophic' ? '#ffffff' : '#721c24'
              }}>
                {((incident.severity || '').charAt(0).toUpperCase() + (incident.severity || '').slice(1))}
              </span>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Status</label>
              <span style={{
                padding: '4px 8px',
                borderRadius: 4,
                fontSize: 14,
                                        background: (incident.status || '') === 'active' ? '#fff3e0' : 
                                   (incident.status || '') === 'resolved' ? '#e8f5e8' : 
                                   (incident.status || '') === 'responding' ? '#e3f2fd' : '#fce8e6',
                        color: (incident.status || '') === 'active' ? '#f57c00' : 
                               (incident.status || '') === 'resolved' ? '#2e7d32' : 
                               (incident.status || '') === 'responding' ? '#1976d2' : '#d32f2f'
              }}>
                {((incident.status || '').charAt(0).toUpperCase() + (incident.status || '').slice(1))}
              </span>
            </div>
            <div style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Reported On</label>
              <div style={{ fontSize: 16 }}>
                {new Date(incident.created_at).toLocaleString()}
              </div>
            </div>
            <div style={{
              marginBottom: 16,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 32,
              alignItems: 'flex-start'
            }}>
              <div style={{ minWidth: '120px' }}>
                <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Upvotes</label>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#19c37d', fontWeight: 600, fontSize: 16 }}>
                  <FaArrowUp /> {incident.upvotes_count ?? 0}
                </span>
              </div>
              <div style={{ minWidth: '120px' }}>
                <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Downvotes</label>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#e74c3c', fontWeight: 600, fontSize: 16 }}>
                  <FaArrowDown /> {incident.downvotes_count ?? 0}
                </span>
              </div>
              <div style={{ minWidth: '140px' }}>
                <label style={{ display: 'block', color: '#666', marginBottom: 4 }}>Credibility Score</label>
                <span style={{ fontWeight: 600, fontSize: 16, color: '#1976d2' }}>{incident.credibility_score ?? 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Assigned Responders Section */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px #0001', marginTop: 24 }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
            gap: 16
          }}>
            <h2 style={{ fontSize: 24, fontWeight: 'bold', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaUsers style={{ color: '#08b1c7' }} />
              Responding Personnel
            </h2>
            {currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin') && (
              <button
                onClick={() => setShowResponderAssignment(!showResponderAssignment)}
                style={{
                  background: '#08b1c7',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 14,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content'
                }}
              >
                <FaPlus />
                {showResponderAssignment ? 'Hide Assignment' : 'Assign Responders'}
              </button>
            )}
          </div>

          {respondersLoading ? (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '40px',
              color: '#6b7280'
            }}>
              <div style={{ 
                width: '24px', 
                height: '24px', 
                border: '3px solid #e5e7eb',
                borderTop: '3px solid #08b1c7',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: '12px'
              }}></div>
              Loading responder information...
            </div>
          ) : (
            <>
              {/* Currently Assigned Responders */}
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', marginBottom: 12 }}>
                  Currently Assigned ({assignedResponders.length})
                </h3>
                {assignedResponders.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                    {assignedResponders.map((responder) => (
                      <div key={responder.user_id} style={{
                        background: 'linear-gradient(145deg, #f0f9ff, #e0f2fe)',
                        border: '1px solid #bae6fd',
                        borderRadius: 12,
                        padding: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <FaUser style={{ color: '#08b1c7', fontSize: 14 }} />
                            <span style={{ fontWeight: 600, color: '#1a1a1a' }}>
                              {responder.first_name} {responder.last_name}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>
                            {responder.assignment_type}
                          </div>
                          {responder.id && (
                            <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
                              ID: {responder.id}
                            </div>
                          )}
                        </div>
                        {currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin') && (
                          <button
                            onClick={() => removeResponderFromIncident(responder)}
                            disabled={respondersLoading}
                            style={{
                              background: '#dc2626',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 8px',
                              borderRadius: 6,
                              cursor: respondersLoading ? 'not-allowed' : 'pointer',
                              fontSize: 12,
                              opacity: respondersLoading ? 0.6 : 1
                            }}
                            title="Remove from incident"
                          >
                            <FaTimesCircle />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center',
                    color: '#6b7280',
                    padding: '32px',
                    background: '#f9fafb',
                    borderRadius: 8,
                    border: '1px dashed #d1d5db'
                  }}>
                    <FaUsers style={{ fontSize: 32, marginBottom: 8, color: '#d1d5db' }} />
                    <p style={{ margin: 0, fontStyle: 'italic' }}>
                      No responders currently assigned to this incident.
                    </p>
                  </div>
                )}
              </div>

              {/* Responder Assignment Interface */}
              {showResponderAssignment && currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin') && (
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e1e8ed',
                  borderRadius: 12,
                  padding: 20
                }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', marginBottom: 16 }}>
                    Available Responders
                  </h3>
                  {availableResponders.length > 0 ? (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                      gap: 12,
                      maxHeight: '300px',
                      overflowY: 'auto'
                    }}>
                      {availableResponders
                        .filter(responder => !assignedResponders.some(assigned => assigned.user_id === responder.user_id))
                        .map((responder) => (
                        <div key={responder.id} style={{
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                          padding: 12,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                              <FaIdBadge style={{ color: '#08b1c7', fontSize: 12 }} />
                              <span style={{ fontWeight: 500, color: '#1a1a1a', fontSize: 14 }}>
                                {responder.first_name} {responder.last_name}
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>
                              {responder.profession}
                            </div>
                            <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
                              {responder.responder_id}
                            </div>
                          </div>
                          <button
                            onClick={() => assignResponderToIncident(responder)}
                            disabled={respondersLoading}
                            style={{
                              background: '#10b981',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: 6,
                              cursor: respondersLoading ? 'not-allowed' : 'pointer',
                              fontSize: 12,
                              fontWeight: 500,
                              opacity: respondersLoading ? 0.6 : 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                            title="Assign to incident"
                          >
                            <FaCheckCircle />
                            Assign
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ 
                      textAlign: 'center',
                      color: '#6b7280',
                      padding: '20px'
                    }}>
                      <p style={{ margin: 0, fontStyle: 'italic' }}>
                        No available responders found.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Edit Form Modal */}
        {showForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              width: '90%',
              maxWidth: 600,
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ margin: 0 }}>Edit Incident Report</h2>
                <button 
                  onClick={() => setShowForm(false)}
                  style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Type</label>
                  <select
                    value={form.type}
                    onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}
                    required
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                  >
                    <option value="">Select Type</option>
                    <option value="fire">Fire</option>
                    <option value="flood">Flood</option>
                    <option value="medical">Medical</option>
                    <option value="rescue">Rescue</option>
                    <option value="others">Others</option>
                  </select>
                </div>
                
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Location</label>
                  {isLoaded ? (
                    <Autocomplete
                      onLoad={autocomplete => {
                        if (window && window.autocompleteRef) {
                          window.autocompleteRef.current = autocomplete;
                        }
                      }}
                      onPlaceChanged={() => {
                        if (window && window.autocompleteRef && window.autocompleteRef.current) {
                          const place = window.autocompleteRef.current.getPlace();
                          if (place.geometry) {
                            setForm(prev => ({
                              ...prev,
                              location: place.formatted_address,
                              location_coords: {
                                latitude: place.geometry.location.lat(),
                                longitude: place.geometry.location.lng()
                              }
                            }));
                          }
                        }
                      }}
                      restrictions={{ country: 'ph' }}
                    >
                      <input
                        type="text"
                        value={form.location}
                        onChange={e => setForm(prev => ({ ...prev, location: e.target.value }))}
                        required
                        placeholder="Search for a location"
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      type="text"
                      value={form.location}
                      onChange={e => setForm(prev => ({ ...prev, location: e.target.value }))}
                      required
                      placeholder="Search for a location"
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                    />
                  )}
                  <button 
                    type="button"
                    onClick={() => {
                      if (navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition(
                          async (position) => {
                            const { latitude, longitude } = position.coords;
                            if (window.google && window.google.maps) {
                              const geocoder = new window.google.maps.Geocoder();
                              try {
                                const result = await geocoder.geocode({
                                  location: { lat: latitude, lng: longitude }
                                });
                                if (result.results[0]) {
                                  setForm(prev => ({
                                    ...prev,
                                    location: result.results[0].formatted_address,
                                    location_coords: {
                                      latitude,
                                      longitude
                                    }
                                  }));
                                }
                              } catch (error) {
                                alert('Error getting address from coordinates');
                              }
                            }
                          },
                          (error) => {
                            alert('Error getting your current location');
                          }
                        );
                      } else {
                        alert('Geolocation is not supported by your browser');
                      }
                    }}
                    style={{
                      marginTop: 8,
                      padding: '8px 16px',
                      borderRadius: 8,
                      border: '1px solid #19c37d',
                      background: '#e6fff4',
                      color: '#19c37d',
                      cursor: 'pointer'
                    }}
                  >
                    Use Current Location
                  </button>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Details</label>
                  <textarea
                    value={form.details}
                    onChange={e => setForm(prev => ({ ...prev, details: e.target.value }))}
                    required
                    style={{ width: '100%', maxWidth: 580, minWidth: 580, padding: 8, borderRadius: 4, border: '1px solid #ccc', minHeight: 100, maxHeight: 500 }}
                  />
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Severity</label>
                  <select
                    value={form.severity}
                    onChange={e => setForm(prev => ({ ...prev, severity: e.target.value }))}
                    required
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                  >
                    <option value="minimal">Minimal</option>
                    <option value="moderate">Moderate</option>
                    <option value="severe">Severe</option>
                    <option value="critical">Critical</option>
                    <option value="catastrophic">Catastrophic</option>
                  </select>
                </div>
                {true && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 8 }}>Status</label>
                    <select
                      value={form.status}
                      onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                    >
                      <option value="active">Active</option>
                      <option value="responding">Responding</option>
                      <option value="resolved">Resolved</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                )}
                <div style={{ marginBottom: 24 }}>
                  <label
                    htmlFor="incident-image-upload"
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      background: '#fff',
                      color: '#19c37d',
                      border: '1px solid #19c37d',
                      borderRadius: 8,
                      cursor: 'pointer',
                      marginBottom: 8
                    }}
                  >
                    Upload Image
                  </label>
                  <input
                    id="incident-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  {form.image_url && (
                    <img
                      src={form.image_url}
                      alt="Preview"
                      style={{ width: '100%', height: 200, objectFit: 'cover', marginTop: 8, borderRadius: 4 }}
                    />
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    style={{
                      padding: '12px 24px',
                      borderRadius: 8,
                      border: '1px solid #ccc',
                      background: '#fff',
                      cursor: 'pointer',
                      fontSize: 16
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '12px 24px',
                      borderRadius: 8,
                      border: 'none',
                      background: '#19c37d',
                      color: '#fff',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: 16,
                      fontWeight: 'bold',
                      opacity: loading ? 0.7 : 1
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default IncidentReportDetail;